import { Injectable } from '@angular/core';
import {
    BehaviorSubject,
    Observable,
    Subject,
    Subscription,
    fromEvent,
    interval,
    merge,
    timer,
} from 'rxjs';
import { first, tap } from 'rxjs/operators';
import { UserSessionPingResponse, UserSessionStatusEnum } from 'core/models';
import { ApiUserSessionService } from './api/api.user-session.service';

export interface SessionIdleMonitorState {
    enabled: boolean;
    warning: boolean;
    remainingSeconds: number | null;
    expiresAt: number | null;
    lastActivityAt: string | null;
    status: UserSessionStatusEnum | null;
}

interface SessionIdleSyncMessage {
    messageId: string;
    sourceId: string;
    type: 'state' | 'stop' | 'lock';
    expiresAt?: number | null;
    lastActivityAt?: string | null;
    status?: UserSessionStatusEnum | null;
    idleTimeoutMinutes?: number;
}

interface SessionIdleLockEvent {
    fromSync: boolean;
    minutes: number;
}

@Injectable({
    providedIn: 'root',
})
export class SessionIdleMonitorService {
    public readonly warningThresholdSeconds = 60;
    public readonly state$ = new BehaviorSubject<SessionIdleMonitorState>({
        enabled: false,
        warning: false,
        remainingSeconds: null,
        expiresAt: null,
        lastActivityAt: null,
        status: null,
    });
    public readonly lock$ = new Subject<SessionIdleLockEvent>();
    public readonly stop$ = new Subject<void>();

    private readonly channelName = 'dcgov-session-idle';
    private readonly storageSyncKey = 'dcgovSessionIdleSync';
    private readonly tabId = this.createTabId();
    private readonly channel = this.createChannel();
    private readonly handledSyncMessageIds = new Set<string>();
    private readonly maxHandledSyncMessageIds = 100;
    private countdownSubscription = new Subscription();
    private warningTimerSubscription = new Subscription();
    private lockTimerSubscription = new Subscription();
    private monitorVersion = 0;
    private monitoring = false;
    private expiresInSeconds: number | null = null;
    private expiresAt: number | null = null;
    private lastActivityAt: string | null = null;
    private status: UserSessionStatusEnum | null = null;
    private locked = false;

    constructor(private apiUserSessionService: ApiUserSessionService) {
        this.listenSyncMessages();
        this.listenPageResumeEvents();
    }

    public start(): void {
        this.stop(false);
        this.startNewMonitoringVersion();

        this.ping().subscribe({ error: () => undefined });
    }

    public stop(sync = true): void {
        this.monitorVersion += 1;
        this.monitoring = false;
        this.resetTimers();
        this.resetSessionState();
        this.emitState(false);

        if (sync) {
            this.broadcast({ type: 'stop' });
        }
    }

    public ping(): Observable<UserSessionPingResponse> {
        const version = this.monitorVersion;

        return this.apiUserSessionService.ping().pipe(
            first(),
            tap(response => {
                if (!this.isCurrentVersion(version)) {
                    return;
                }

                this.applyPingResponse(response);
            }),
        );
    }

    public getIdleTimeoutMinutes(): number | null {
        if (!this.expiresInSeconds) {
            return null;
        }

        return Math.ceil(this.expiresInSeconds / 60);
    }

    public registerRequestActivity(): void {
        if (!this.monitoring || !this.expiresInSeconds) {
            return;
        }

        this.lastActivityAt = new Date().toISOString();
        this.expiresAt = Date.now() + this.expiresInSeconds * 1000;
        this.locked = false;

        this.scheduleTimers();
        this.emitState(true);
        this.broadcast({ type: 'state' });
    }

    private applyPingResponse(response: UserSessionPingResponse): void {
        this.status = response.status ?? null;
        this.lastActivityAt = response.lastActivityAt ?? new Date().toISOString();
        this.expiresInSeconds = response.expiresInSeconds ?? null;

        if (!this.expiresInSeconds) {
            this.stop(false);
            return;
        }

        this.expiresAt = Date.now() + this.expiresInSeconds * 1000;
        this.locked = false;

        this.scheduleTimers();
        this.emitState(true);
        this.broadcast({ type: 'state' });
    }

    private startNewMonitoringVersion(): void {
        this.monitorVersion += 1;
        this.monitoring = true;
    }

    private isCurrentVersion(version: number): boolean {
        return this.monitoring && this.monitorVersion === version;
    }

    private resetTimers(): void {
        this.countdownSubscription.unsubscribe();
        this.warningTimerSubscription.unsubscribe();
        this.lockTimerSubscription.unsubscribe();
        this.countdownSubscription = new Subscription();
        this.warningTimerSubscription = new Subscription();
        this.lockTimerSubscription = new Subscription();
    }

    private resetSessionState(): void {
        this.expiresInSeconds = null;
        this.expiresAt = null;
        this.lastActivityAt = null;
        this.status = null;
        this.locked = false;
    }

    private startWarningCountdown(version: number): void {
        if (!this.isCurrentVersion(version)) {
            return;
        }

        this.countdownSubscription.unsubscribe();
        this.countdownSubscription = interval(1000).subscribe(() => {
            if (this.isCurrentVersion(version)) {
                this.emitState(true);
            }
        });
    }

    private scheduleTimers(): void {
        this.warningTimerSubscription.unsubscribe();
        this.lockTimerSubscription.unsubscribe();
        this.countdownSubscription.unsubscribe();

        if (!this.expiresAt) {
            return;
        }

        const now = Date.now();
        const warningDelayMs = Math.max(
            0,
            this.expiresAt - now - this.warningThresholdSeconds * 1000,
        );
        const lockDelayMs = Math.max(0, this.expiresAt - now);
        const version = this.monitorVersion;

        this.warningTimerSubscription = timer(warningDelayMs).subscribe(() => {
            if (this.isCurrentVersion(version)) {
                this.startWarningCountdown(version);
                this.emitState(true);
            }
        });

        this.lockTimerSubscription = timer(lockDelayMs).subscribe(() => {
            if (this.isCurrentVersion(version)) {
                this.emitState(true);
            }
        });
    }

    private emitState(enabled: boolean): void {
        const remainingSeconds = this.getRemainingSeconds();

        if (enabled && remainingSeconds === 0 && !this.locked) {
            this.locked = true;
            const idleTimeoutMinutes = this.getIdleTimeoutMinutes() ?? 0;
            this.lock$.next({ fromSync: false, minutes: idleTimeoutMinutes });
            this.stop(false);
            this.broadcast({ type: 'lock', idleTimeoutMinutes });
            return;
        }

        const warning =
            enabled &&
            remainingSeconds !== null &&
            remainingSeconds <= this.warningThresholdSeconds;

        this.state$.next({
            enabled,
            warning,
            remainingSeconds,
            expiresAt: this.expiresAt,
            lastActivityAt: this.lastActivityAt,
            status: this.status,
        });
    }

    private getRemainingSeconds(): number | null {
        if (!this.expiresAt) {
            return null;
        }

        return Math.max(0, Math.ceil((this.expiresAt - Date.now()) / 1000));
    }

    private listenSyncMessages(): void {
        this.channel?.addEventListener('message', event => this.handleSyncMessage(event.data));
        window.addEventListener('storage', event => {
            if (event.key !== this.storageSyncKey || !event.newValue) {
                return;
            }

            try {
                this.handleSyncMessage(JSON.parse(event.newValue) as SessionIdleSyncMessage);
            } catch (e) {
                return;
            }
        });
    }

    private handleSyncMessage(message: SessionIdleSyncMessage): void {
        if (!message || message.sourceId === this.tabId) {
            return;
        }

        if (this.hasHandledSyncMessage(message.messageId)) {
            return;
        }

        if (message.type === 'stop') {
            this.stop(false);
            this.stop$.next();
            return;
        }

        if (message.type === 'lock') {
            this.stop(false);
            this.lock$.next({
                fromSync: true,
                minutes: message.idleTimeoutMinutes ?? 0,
            });
            return;
        }

        if (!this.monitoring || !this.expiresInSeconds) {
            return;
        }

        const nextExpiresAt = message.expiresAt ?? Date.now() + this.expiresInSeconds * 1000;

        if (this.expiresAt && nextExpiresAt < this.expiresAt) {
            return;
        }

        this.expiresAt = nextExpiresAt;
        this.lastActivityAt = message.lastActivityAt ?? this.lastActivityAt;
        this.status = message.status ?? this.status;
        this.locked = false;
        this.scheduleTimers();
        this.emitState(true);
    }

    private listenPageResumeEvents(): void {
        merge(fromEvent(document, 'visibilitychange'), fromEvent(window, 'focus')).subscribe(() => {
            if (!this.monitoring || !this.expiresInSeconds) {
                return;
            }

            this.emitState(true);
        });
    }

    private broadcast(message: Omit<SessionIdleSyncMessage, 'messageId' | 'sourceId'>): void {
        const payload: SessionIdleSyncMessage = {
            messageId: this.createMessageId(),
            sourceId: this.tabId,
            expiresAt: this.expiresAt,
            lastActivityAt: this.lastActivityAt,
            status: this.status,
            ...message,
        };

        this.channel?.postMessage(payload);
        localStorage.setItem(this.storageSyncKey, JSON.stringify(payload));
    }

    private hasHandledSyncMessage(messageId: string): boolean {
        if (this.handledSyncMessageIds.has(messageId)) {
            return true;
        }

        this.handledSyncMessageIds.add(messageId);

        if (this.handledSyncMessageIds.size > this.maxHandledSyncMessageIds) {
            const oldestMessageId = this.handledSyncMessageIds.values().next().value;
            if (oldestMessageId) {
                this.handledSyncMessageIds.delete(oldestMessageId);
            }
        }

        return false;
    }

    private createChannel(): BroadcastChannel | null {
        if (typeof BroadcastChannel === 'undefined') {
            return null;
        }

        return new BroadcastChannel(this.channelName);
    }

    private createTabId(): string {
        if (window.crypto?.randomUUID) {
            return window.crypto.randomUUID();
        }

        return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    }

    private createMessageId(): string {
        return `${this.tabId}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    }
}
